/**
 * ================================================================================================
 *  THE SUPPORT ASSISTANT. **Section 5, rebuilt 2026-10-26 on the Owner's second reading.**
 * ================================================================================================
 *
 *   > *"נדרש כפתור קבוע בצד המסך עם שם הבוט, הפותח חלונית עזרה המבוססת על מדריך המערכת … אין
 *   > להסתפק בכפתור דמה."*
 *
 * and then, having opened the one I built:
 *
 *   > *"למה הבוט שלנו מלא מידע לא קשור במקום לחלק לקטגוריות כמו שצריך? לתת את ה־3-4 הקטגוריות
 *   > החשובות וזהו, ומשם ילחצו וימשיכו."*
 *
 * HE WAS RIGHT, AND THE OLD PANEL IS THE EVIDENCE. It opened with a paragraph about where its
 * answers come from, then the question box, then what the current screen is, then a full entry
 * with its support class and its cause, then two paragraphs about what it cannot see. Every
 * sentence was true. None had been asked for, and together they were a wall.
 *
 * ── WHAT ARRIVES NOW ────────────────────────────────────────────────────────────────────────────
 *
 *   a question box
 *   four categories, ONE LINE EACH, closed
 *   one short line saying where the answers come from
 *
 * Everything else is behind a click. Opening a category lists its answers by title; opening an
 * answer shows its steps and the screen that does the job. The limits — that this panel cannot see
 * the journal, the server or your data — moved behind a control, because they are the
 * qualification on an ANSWER, and printing them over an empty panel is how they stopped being read.
 *
 * The categories are the settings screen's own four. That is deliberate: somebody looking for help
 * about permissions should find it under the word they clicked to get there. A help menu with its
 * own private taxonomy is a second thing to learn.
 *
 * ── WHAT DID NOT CHANGE, AND MUST NOT ───────────────────────────────────────────────────────────
 *
 * GROUNDED             every sentence comes from `support/howTo.ts` and `support/guide.ts`;
 *                      nothing is generated, and the matching runs in THIS BROWSER, so no customer
 *                      text leaves the estate.
 * RESPECTS PERMISSION  an answer whose action needs a capability is hidden from a session without
 *                      it (J.3) — and when that hides a match, the panel SAYS so rather than
 *                      quietly showing a different one.
 * ADMITS NO ANSWER     below its matching threshold it returns nothing and reports that, which is
 *                      J.0 rule 3: say what you could not obtain, and stop.
 * NEVER BUG_CANDIDATE  J.2 lists the inspection points a support surface must hold first — an
 *                      audit row, a journal window, a release identity. This panel holds none.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import logoUrl from '../assets/glpoint-logo.jpeg';
import { useLocale } from '../i18n/LocaleProvider';
import { load, remember, save, type Conversation } from './conversation';
import { SupportReference } from '../components/SupportReference';
import { useWorkContext } from '../shell/ContextProvider';
import { guideFor, visibleEntries, type Bilingual, type GuideEntry } from './guide';
import { CATEGORIES, entriesIn, HOW_TO, searchHowToResult, type HowTo } from './howTo';

export interface SupportAssistantProps {
  /**
   * The screen this panel is standing on. `undefined` ⇒ the screen's own category is not offered.
   *
   * It is `string | undefined` and not an optional property: `exactOptionalPropertyTypes` makes
   * those different types, and the shell passes its own optional `screenId` straight through.
   */
  readonly screenId: string | undefined;
}

export function SupportAssistant({ screenId }: SupportAssistantProps): JSX.Element {
  const { t, locale } = useLocale();
  const ctx = useWorkContext();

  /*
   * ══ THE CONVERSATION SURVIVES A SCREEN CHANGE. **2026-10-28.** ═════════════════════════
   *
   * Owner: *"העוזר עונה נכון ומפנה למסך המתאים, אבל לחיצה על הקישור או מעבר למסך אחר
   * סוגרים אותו ומאבדים את השיחה."*
   *
   * Every link in this product is a plain `<a href>`, so following one of the assistant's own
   * answers is a FULL DOCUMENT LOAD and the whole React tree is rebuilt. The panel was not closing
   * — it was ceasing to exist. `conversation.ts` is where it is kept; the identity is what keeps it
   * from reaching anybody else.
   */
  const identity = {
    userUid: ctx.session?.userUid ?? null,
    tenantUid: ctx.tenant?.tenantUid ?? ctx.session?.activeTenantUid ?? null,
  };

  /*
   * READ ONCE, LAZILY. `useState(initialiser)` runs on the first render only, which is what makes
   * this a restore rather than a read on every keystroke. The session may still be `null` on that
   * first render; the effect below re-reads when it arrives, and the comparison in `load` is what
   * stops a conversation written under one identity being shown under another.
   */
  const [conversation, setConversation] = useState<Conversation>(() => load(identity));

  useEffect(() => {
    setConversation(load({ userUid: identity.userUid, tenantUid: identity.tenantUid }));
    // The identity is the dependency: a sign-in, a sign-out or an office switch re-reads, and a
    // mismatch yields an empty conversation rather than somebody else's.
  }, [identity.userUid, identity.tenantUid]);

  const open = conversation.open;
  const setOpen = (next: boolean): void => {
    const updated = { open: next, exchanges: conversation.exchanges };
    setConversation(updated);
    save(identity, updated);
  };

  /** Which category is expanded, or `null`. One at a time: two open is the wall again. */
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  /** Which answer is expanded inside it. Cleared whenever the category changes. */
  const [openAnswer, setOpenAnswer] = useState<string | null>(null);
  /** The limits, behind a control rather than printed over every arrival. */
  const [limitsShown, setLimitsShown] = useState(false);

  /*
   * `asked` is the question that was SUBMITTED, kept apart from what is being typed. Searching on
   * every keystroke would flicker through wrong ANSWERS while somebody is still writing the word.
   *
   * ── AND SUGGESTIONS ARE NOT ANSWERS, WHICH IS WHY THEY MAY START AT THE FIRST LETTER ─────
   *
   * The Owner: *"הבוט צריך להציע אפשרויות מההקלדה הראשונה."* An answer REPLACES the panel and
   * is read; a suggestion is a TITLE in a short list that narrows as the words arrive, opens
   * nothing by itself, and costs a reader nothing when it is wrong. The objection above stands
   * for answers and does not reach these.
   */
  const [typed, setTyped] = useState('');

  /*
   * THE QUESTION BEING ANSWERED IS THE LAST ONE IN THE CONVERSATION, not a separate piece of state.
   * Holding it twice is how the panel came back showing an answer to a question its own history did
   * not contain.
   */
  const asked = conversation.exchanges.at(-1)?.question ?? null;

  /** One sentence in the active language. Both are written; nothing is translated at runtime. */
  const say = (value: Bilingual): string => (locale === 'en' ? value.en : value.he);

  /*
   * BROWSING THE CATEGORIES INSTEAD OF READING THE LAST ANSWER. It is a VIEW and not a fact, so it
   * is ordinary component state: a reload legitimately returns to the answer, which is the
   * conversation the reader left.
   */
  const [browsing, setBrowsing] = useState(false);

  const capabilities = ctx.session?.capabilities ?? [];
  /*
   * WHERE A PERSON REACHES A PERSON, FROM THE SERVER. `whoami` carries it because
   * `mail-identity-is-configuration` forbids an address literal anywhere under `apps/web/src` --
   * and it is right: an address in a component is an address nobody can change without a release.
   * `null` is an estate with no transport configured, and the panel says so rather than rendering
   * a `mailto:` that goes nowhere.
   */
  const supportAddress = ctx.session?.supportContactAddress ?? null;
  /*
   * WHAT THE PERSON QUOTES. The signed-in account's own uid, which is what a support reader can
   * find a session by -- not a request uid, because nothing here made a request that failed.
   * Empty is a real state and `SupportReference` writes it out rather than hiding the control.
   */
  const reference = identity.userUid ?? '';
  const showing = browsing ? null : asked;
  const found = showing === null ? { shown: [], withheld: 0 } : searchHowToResult(showing, capabilities);

  /*
   * ══ WHAT TO OFFER WHILE SOMEBODY IS STILL TYPING. **2026-11-03.** ══════════════════
   *
   * The SAME matcher the submitted question uses, so a suggestion a reader clicks gives the answer
   * the panel would have given anyway — a second ranking here would be a second opinion about
   * which answer is best, and the one that drifts.
   *
   * IT IS SILENT WHILE THE ANSWER IS ON SCREEN. Once a question has been submitted the reader is
   * reading; a list of other titles under the box would be the panel talking over its own answer.
   */
  const suggestions =
    typed.trim() === '' || (showing !== null && typed === showing)
      ? []
      : searchHowToResult(typed, capabilities).shown.slice(0, 3);

  /*
   * ══ `אחר` — AND WHAT HAPPENS AFTER IT. ═══════════════════════════════════════════
   *
   * The Owner: *"אם בוחרים אחר — שימשיך להציע."* Choosing *other* used to be unreachable and
   * reaching no answer used to be a dead end: the panel said *"I have no grounded answer"* and
   * stopped.
   *
   * SO `other` IS A CATEGORY THAT KEEPS THE PANEL OPEN rather than a closing door. It offers the
   * whole table's titles — everything this session may act on — because a reader who has told us
   * their words did not match is better served by the list than by a better guess at their words.
   * NOTHING IS GENERATED: every title is a row in `HOW_TO`, which is what `GROUNDED` in the header
   * means and is not relaxed here.
   */
  const everything: readonly HowTo[] = HOW_TO.filter(
    (entry: HowTo) => entry.needs === undefined || capabilities.includes(entry.needs),
  );

  /** The screen's own entries, offered as the FIRST category when there are any. */
  const guide = guideFor(screenId);
  const screenEntries = guide === null ? [] : visibleEntries(guide, capabilities);

  function ask(e: FormEvent): void {
    e.preventDefault();
    askFor(typed);
  }

  /**
   * ASK A QUESTION, however it arrived — typed and submitted, or chosen from the suggestions.
   *
   * ONE PATH, so a suggestion lands in the conversation exactly as a typed question does: with the
   * screen it was asked from, appended and not replacing the history. A suggestion that opened an
   * answer WITHOUT recording the question would leave a panel showing an answer its own history
   * does not contain — the state this file already fixed once.
   */
  function askFor(question: string): void {
    if (question.trim() === '') return;
    setTyped(question);
    /*
     * APPENDED, NOT REPLACED. *"לעדכן את הקשר המסך לשאלה הבאה, בלי למחוק את ההיסטוריה."* The
     * exchange records the screen it was asked FROM, so the next question follows the screen the
     * reader has moved to while the ones before it stay as they were asked.
     */
    const updated = remember(conversation, {
      question,
      screenId,
      atMs: Date.now(),
    });
    setConversation(updated);
    save(identity, updated);
    setOpenCategory(null);
    setOpenAnswer(null);
    // ASKING LEAVES THE CATEGORIES. They are two ways into one table, and both at once is the wall.
    setBrowsing(false);
  }

  function toggleCategory(id: string): void {
    setOpenCategory((was) => (was === id ? null : id));
    setOpenAnswer(null);
    /*
     * A question and a category are two ways into the same table; both at once is the wall again.
     * SO THE ANSWERS STEP ASIDE — AND THE HISTORY DOES NOT. This used to clear the question
     * itself; now it closes the last exchange, which leaves the conversation intact and is what
     * *"בלי למחוק את ההיסטוריה"* asks for.
     */
    setBrowsing(true);
  }

  /** One answer, open: the steps in order, and where the job is done. */
  const renderAnswer = (entry: HowTo): JSX.Element => (
    <div className="assistant__answer">
      <ol className="assistant__steps">
        {entry.steps.map((step) => (
          <li key={step.he}>{say(step)}</li>
        ))}
      </ol>
      {entry.href === undefined ? null : (
        <a className="btn btn--nav" href={entry.href}>
          {t('assistant.goThere')}
        </a>
      )}
    </div>
  );

  /*
   * ONE SCREEN-GUIDE ENTRY, OPEN. It used to end at a sentence naming a screen and leave the
   * reader to go find it. The Owner: *"the customer asks a question or picks a category, and can
   * click the link and it takes them to the right page."* So where the entry carries a
   * destination, the same button appears here as on a how-to -- one control, two tables.
   */
  const renderGuideEntry = (entry: GuideEntry): JSX.Element => (
    <div className="assistant__answer">
      <p className="assistant__because">{say(entry.because)}</p>
      <p className="assistant__next">{say(entry.next)}</p>
      {entry.href === undefined ? null : (
        <a className="btn btn--nav" href={entry.href}>
          {t('assistant.goThere')}
        </a>
      )}
    </div>
  );

  return (
    <>
      {/*
        THE LAUNCHER IS A SMALL MESSAGE BUBBLE and nothing else. The Owner asked for it in those
        words -- *"a small message button, and then the categories underneath; the logo should be
        there"* -- and a round mark is what a person already reads as "ask something here".
        Dropping the word means the button must carry its name another way, so it is the accessible
        name and the hover title: a circle with no accessible name is a button nobody can describe.
      */}
      <button
        type="button"
        className="assistant-launch"
        aria-expanded={open}
        aria-controls="assistant-panel"
        aria-label={t('assistant.name')}
        title={t('assistant.name')}
        onClick={() => setOpen(!open)}
      >
        <svg
          className="assistant-launch__bubble"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          {/* One speech bubble with a tail, drawn in the current colour so it follows the theme. */}
          <path
            d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V5a1 1 0 0 1 1-1z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {!open ? null : (
        <aside
          id="assistant-panel"
          className="assistant"
          role="dialog"
          aria-label={t('assistant.name')}
        >
          <header className="assistant__head">
            <h2 className="assistant__title">{t('assistant.name')}</h2>
            <button type="button" className="btn btn--quiet" onClick={() => setOpen(false)}>
              {t('assistant.close')}
            </button>
          </header>

          <form className="assistant__ask" onSubmit={ask}>
            <label className="field">
              <span className="field__label">{t('assistant.askLabel')}</span>
              <input
                className="field__input"
                name="assistant-question"
                value={typed}
                placeholder={t('assistant.askPlaceholder')}
                onChange={(e) => setTyped(e.currentTarget.value)}
              />
            </label>
            <button type="submit" className="btn btn--primary" disabled={typed.trim() === ''}>
              {t('assistant.askAction')}
            </button>
          </form>

          {/*
            ══ FROM THE FIRST KEYSTROKE. *"\u05d4\u05d1\u05d5\u05d8 \u05e6\u05e8\u05d9\u05da \u05dc\u05d4\u05e6\u05d9\u05e2 \u05d0\u05e4\u05e9\u05e8\u05d5\u05d9\u05d5\u05ea \u05de\u05d4\u05d4\u05e7\u05dc\u05d3\u05d4 \u05d4\u05e8\u05d0\u05e9\u05d5\u05e0\u05d4."* ════

            TITLES ONLY, AT MOST THREE, AND THEY OPEN NOTHING BY THEMSELVES. A suggestion costs a
            reader nothing when it is wrong, which is why these may start before the word is
            finished while the ANSWERS still wait for the submit. Pressing one asks that question,
            so the reader lands on the same answer the panel would have given.

            `aria-live="polite"`, so a list that changes under somebody's hands is announced once
            it settles rather than on every letter.
          */}
          {suggestions.length === 0 ? null : (
            <section className="assistant__block" aria-live="polite" data-area="suggestions">
              <h3 className="assistant__heading">{t('assistant.suggestTitle')}</h3>
              <ul className="assistant__topics">
                {suggestions.map(({ entry }) => (
                  <li key={entry.id} className="assistant__topic">
                    <button
                      type="button"
                      className="assistant__topic-row"
                      onClick={() => askFor(say(entry.title))}
                    >
                      {say(entry.title)}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {showing !== null ? (
            /* ── A QUESTION WAS ASKED. Its answers REPLACE the categories, never join them. ──── */
            found.shown.length === 0 ? (
              <section className="assistant__block" data-tone="empty" data-area="no-answer">
                <h3 className="assistant__heading">{t('assistant.noAnswerTitle')}</h3>
                <p>{t('assistant.noAnswerBody')}</p>
                {/*
                  ══ AND IT KEEPS OFFERING. *"\u05d0\u05dd \u05d1\u05d5\u05d7\u05e8\u05d9\u05dd \u05d0\u05d7\u05e8 \u2014 \u05e9\u05d9\u05de\u05e9\u05d9\u05da \u05dc\u05d4\u05e6\u05d9\u05e2."* ═══════════

                  This branch used to end here, at a sentence. A reader whose words did not match
                  was told so and left with a text box and the same words.

                  The whole table is offered instead — every title this session may act on —
                  because somebody who has already failed to describe their problem is better
                  served by a LIST than by a better guess at their phrasing. Nothing is generated:
                  each is a row in `HOW_TO`, which is what GROUNDED in the header means.
                */}
                <OtherTopics entries={everything} say={say} onAsk={askFor} />
                <ContactPath address={supportAddress} reference={reference} t={t} />
              </section>
            ) : (
              <>
                {found.shown.map(({ entry }) => (
                  <section key={entry.id} className="assistant__topic">
                    <h3 className="assistant__heading">{say(entry.title)}</h3>
                    {renderAnswer(entry)}
                  </section>
                ))}
                {found.withheld === 0 ? null : (
                  // AN ANSWER EXISTS AND THIS SESSION MAY NOT ACT ON IT. Without this line a
                  // substitution looks exactly like a reply.
                  <p className="assistant__withheld">{t('assistant.withheld')}</p>
                )}
              </>
            )
          ) : (
            /* ── NOTHING ASKED: the categories, closed. This is the whole panel on arrival. ──── */
            <ul className="assistant__categories">
              {screenEntries.length === 0 ? null : (
                <li className="assistant__category">
                  <button
                    type="button"
                    className="assistant__category-row"
                    aria-expanded={openCategory === 'screen'}
                    onClick={() => toggleCategory('screen')}
                  >
                    {t('assistant.thisScreen')}
                    <span className="assistant__chevron" aria-hidden="true" />
                  </button>
                  {openCategory !== 'screen' ? null : (
                    <ul className="assistant__topics">
                      {screenEntries.map((entry) => (
                        <li key={entry.symptom.he} className="assistant__topic">
                          <button
                            type="button"
                            className="assistant__topic-row"
                            aria-expanded={openAnswer === entry.symptom.he}
                            onClick={() =>
                              setOpenAnswer((was) =>
                                was === entry.symptom.he ? null : entry.symptom.he,
                              )
                            }
                          >
                            {say(entry.symptom)}
                          </button>
                          {openAnswer !== entry.symptom.he ? null : renderGuideEntry(entry)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}

              {CATEGORIES.map((category) => {
                const entries = entriesIn(category, capabilities);
                // A category this session can do nothing in is not offered at all. An empty list
                // opened is worse than a row that was never there.
                if (entries.length === 0) return null;
                return (
                  <li key={category.id} className="assistant__category">
                    <button
                      type="button"
                      className="assistant__category-row"
                      aria-expanded={openCategory === category.id}
                      onClick={() => toggleCategory(category.id)}
                    >
                      {say(category.title)}
                      <span className="assistant__chevron" aria-hidden="true" />
                    </button>
                    {openCategory !== category.id ? null : (
                      <ul className="assistant__topics">
                        {entries.map((entry) => (
                          <li key={entry.id} className="assistant__topic">
                            <button
                              type="button"
                              className="assistant__topic-row"
                              aria-expanded={openAnswer === entry.id}
                              onClick={() =>
                                setOpenAnswer((was) => (was === entry.id ? null : entry.id))
                              }
                            >
                              {say(entry.title)}
                            </button>
                            {openAnswer !== entry.id ? null : renderAnswer(entry)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
              {/*
                `\u05d0\u05d7\u05e8` IS LAST, AND IT IS NOT A FIFTH TOPIC. The four above are the settings screen's
                own, deliberately: somebody looking for help about permissions should find it under
                the word they clicked to get there. This one is for the reader whose problem is
                under none of them, and what it opens is the whole table plus the way to reach a
                person -- so choosing it CONTINUES rather than closing.
              */}
              <li className="assistant__category" data-area="other">
                <button
                  type="button"
                  className="assistant__category-row"
                  aria-expanded={openCategory === 'other'}
                  onClick={() => toggleCategory('other')}
                >
                  {t('assistant.otherCategory')}
                  <span className="assistant__chevron" aria-hidden="true" />
                </button>
                {openCategory !== 'other' ? null : (
                  <>
                    <OtherTopics entries={everything} say={say} onAsk={askFor} />
                    <ContactPath address={supportAddress} reference={reference} t={t} />
                  </>
                )}
              </li>
            </ul>
          )}

          {/*
            THE PRODUCT MARK SITS UNDER THE CATEGORIES, which is where the Owner's screenshot has
            it and not where I first put it. Same asset and same `alt` as the shell header: `alt`
            is the product name because the mark IS the name, and a screen reader that said "logo"
            would be describing the file rather than reading the word on it.
          */}
          <div className="assistant__mark">
            <img src={logoUrl} alt="GL-POINT" />
          </div>

          <footer className="assistant__limit">
            {/*
              WHERE THE ANSWERS COME FROM stays, in one line: a help surface that does not say
              whether it is quoting or guessing is one nobody can weigh. WHAT IT CANNOT SEE moved
              behind this control.
            */}
            <p className="assistant__provenance">{t('assistant.provenance')}</p>
            <button
              type="button"
              className="btn btn--quiet"
              aria-expanded={limitsShown}
              onClick={() => setLimitsShown((was) => !was)}
            >
              {t('assistant.limitsToggle')}
            </button>
            {!limitsShown ? null : (
              <>
                <p>{t('assistant.limit')}</p>
                <p>{t('assistant.referenceCode')}</p>
              </>
            )}
          </footer>
        </aside>
      )}
    </>
  );
}

/**
 * EVERY TITLE THIS SESSION MAY ACT ON. The `אחר` path, and the no-answer path.
 *
 * A LIST AND NOT A BETTER GUESS. A reader who reaches either of these has already failed to
 * describe their problem in words that matched, so asking them for better words is asking for the
 * thing they just could not produce. Titles are cheap to scan and each one asks its own question
 * through the SAME path a typed question takes.
 *
 * PERMISSION FILTERED BY THE CALLER, which is why this takes entries and not capabilities: J.3
 * says an answer the session cannot act on is not a better answer than silence, and the filter
 * lives once, beside the capability set.
 */
function OtherTopics({
  entries,
  say,
  onAsk,
}: {
  readonly entries: readonly HowTo[];
  readonly say: (value: Bilingual) => string;
  readonly onAsk: (question: string) => void;
}): JSX.Element {
  return (
    <ul className="assistant__topics" data-area="other-topics">
      {entries.map((entry) => (
        <li key={entry.id} className="assistant__topic">
          <button
            type="button"
            className="assistant__topic-row"
            onClick={() => onAsk(say(entry.title))}
          >
            {say(entry.title)}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * THE WAY OUT OF THE PANEL. **The Owner: *"\u05de\u05e1\u05dc\u05d5\u05dc \u05e4\u05e0\u05d9\u05d9\u05d4 \u05d7\u05d5\u05e4\u05e9\u05d9 \u05db\u05e9\u05d4\u05ea\u05e9\u05d5\u05d1\u05d5\u05ea \u05d0\u05d9\u05e0\u05df \u05e2\u05d5\u05d6\u05e8\u05d5\u05ea."***
 *
 * A grounded panel that can only answer from a table WILL reach questions the table does not hold
 * -- that is the price of refusing to generate, and it is the right price. What it must not do is
 * end there.
 *
 * ── IT IS A `mailto:`, AND THAT IS A DECISION AND NOT A SHORTCUT ────────────────────────
 *
 * The alternative was a route that posts a message: a capability, a store, a queue and a mailbox
 * somebody has to watch -- and until somebody watches it, a form that swallows a customer's
 * problem is worse than no form. The reply-to address is already the one every message this
 * product sends tells people to answer, so a person writing to it reaches whoever already reads
 * it. When the Owner wants a tracked queue instead, that is a decision with an owner and a
 * mailbox, not a component.
 *
 * NOTHING ABOUT THE CUSTOMER IS PUT IN THE URL. The subject carries the product name and the
 * account reference; no screen name, no question text and no tenant. A `mailto:` is a URL the
 * browser may log and the Owner's own rule is that personal data does not travel in one.
 */
function ContactPath({
  address,
  reference,
  t,
}: {
  readonly address: string | null;
  readonly reference: string;
  readonly t: (key: 'assistant.contactTitle' | 'assistant.contactBody' | 'assistant.contactAction' | 'assistant.contactNone' | 'assistant.contactSubject') => string;
}): JSX.Element {
  return (
    <section className="assistant__block" data-area="contact">
      <h3 className="assistant__heading">{t('assistant.contactTitle')}</h3>
      <p>{t('assistant.contactBody')}</p>
      {address === null ? (
        /*
         * NO ADDRESS IS CONFIGURED ON THIS ESTATE, and that is said rather than papered over with
         * the sending address -- which `MailIdentity` itself calls *"routinely unmonitored"*.
         * Pointing somebody who cannot get help at an unread mailbox is worse than telling them
         * there is nowhere to write.
         */
        <p className="assistant__withheld">{t('assistant.contactNone')}</p>
      ) : (
        /*
         * ── THE TREATMENT IS `btn--nav`, AND THE REASON SITS ABOVE THE ELEMENT ──────────────
         *
         * `actionAffordance.test.ts`: *"NAVIGATION IS NOT AN ACTION"* — and this one leaves the
         * product entirely, which is the strongest case for that rule rather than an exception to
         * it. The reader is going somewhere, not causing something here.
         *
         * AND THE PARAGRAPH IS OUTSIDE THE TAG because the check reads the text that FOLLOWS `<a`,
         * so a comment between the tag and its attributes naming the forbidden class is a finding
         * about itself — the same shape `beginRun({` in a comment produced for G-12 this morning.
         */
        <a
          className="btn btn--nav"
          href={`mailto:${address}?subject=${encodeURIComponent(`${t('assistant.contactSubject')} ${reference}`)}`}
        >
          {t('assistant.contactAction')}
        </a>
      )}
      <SupportReference requestUid={reference} />
    </section>
  );
}
